<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import PauseTemplatesAPI from 'dashboard/api/pauseTemplates';

const emit = defineEmits(['updated']);
const { t } = useI18n();

const templates = ref([]);
const isFetching = ref(false);
const isSaving = ref(false);
const deletingId = ref(null);

const form = reactive({
  id: null,
  name: '',
  durationInMinutes: 10,
  dailyLimit: 1,
  requiresSupervisorConfirmation: false,
});

const isEditing = computed(() => Boolean(form.id));

const resetForm = () => {
  Object.assign(form, {
    id: null,
    name: '',
    durationInMinutes: 10,
    dailyLimit: 1,
    requiresSupervisorConfirmation: false,
  });
};

const fetchTemplates = async () => {
  isFetching.value = true;

  try {
    const response = await PauseTemplatesAPI.list();
    templates.value = response.data;
  } catch (error) {
    useAlert(t('AGENT_MGMT.PAUSE_TEMPLATES.API.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const validateForm = () => {
  if (!form.name.trim()) {
    useAlert(t('AGENT_MGMT.PAUSE_TEMPLATES.FORM.NAME.ERROR'));
    return false;
  }

  if (Number(form.durationInMinutes) <= 0) {
    useAlert(t('AGENT_MGMT.PAUSE_TEMPLATES.FORM.DURATION.ERROR'));
    return false;
  }

  if (Number(form.dailyLimit) <= 0) {
    useAlert(t('AGENT_MGMT.PAUSE_TEMPLATES.FORM.DAILY_LIMIT.ERROR'));
    return false;
  }

  return true;
};

const saveTemplate = async () => {
  if (!validateForm()) return;

  isSaving.value = true;

  const payload = {
    name: form.name.trim(),
    duration_in_minutes: Number(form.durationInMinutes),
    daily_limit: Number(form.dailyLimit),
    requires_supervisor_confirmation: form.requiresSupervisorConfirmation,
  };

  try {
    if (isEditing.value) {
      await PauseTemplatesAPI.update(form.id, payload);
      useAlert(t('AGENT_MGMT.PAUSE_TEMPLATES.API.UPDATE_SUCCESS'));
    } else {
      await PauseTemplatesAPI.create(payload);
      useAlert(t('AGENT_MGMT.PAUSE_TEMPLATES.API.CREATE_SUCCESS'));
    }

    resetForm();
    await fetchTemplates();
    emit('updated');
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('AGENT_MGMT.PAUSE_TEMPLATES.API.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const editTemplate = template => {
  Object.assign(form, {
    id: template.id,
    name: template.name,
    durationInMinutes: template.duration_in_minutes,
    dailyLimit: template.daily_limit,
    requiresSupervisorConfirmation: template.requires_supervisor_confirmation,
  });
};

const deleteTemplate = async template => {
  deletingId.value = template.id;

  try {
    await PauseTemplatesAPI.delete(template.id);
    useAlert(t('AGENT_MGMT.PAUSE_TEMPLATES.API.DELETE_SUCCESS'));
    await fetchTemplates();
    emit('updated');
    if (form.id === template.id) resetForm();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('AGENT_MGMT.PAUSE_TEMPLATES.API.DELETE_ERROR')
    );
  } finally {
    deletingId.value = null;
  }
};

onMounted(fetchTemplates);
</script>

<template>
  <div class="flex flex-col h-auto max-h-[85vh] overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.PAUSE_TEMPLATES.TITLE')"
      :header-content="$t('AGENT_MGMT.PAUSE_TEMPLATES.DESCRIPTION')"
    />

    <form class="flex flex-col gap-4 p-1" @submit.prevent="saveTemplate">
      <label>
        {{ $t('AGENT_MGMT.PAUSE_TEMPLATES.FORM.NAME.LABEL') }}
        <input
          v-model="form.name"
          type="text"
          :placeholder="$t('AGENT_MGMT.PAUSE_TEMPLATES.FORM.NAME.PLACEHOLDER')"
        />
      </label>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label>
          {{ $t('AGENT_MGMT.PAUSE_TEMPLATES.FORM.DURATION.LABEL') }}
          <input v-model="form.durationInMinutes" min="1" type="number" />
        </label>

        <label>
          {{ $t('AGENT_MGMT.PAUSE_TEMPLATES.FORM.DAILY_LIMIT.LABEL') }}
          <input v-model="form.dailyLimit" min="1" type="number" />
        </label>
      </div>

      <label class="flex items-center gap-2">
        <input v-model="form.requiresSupervisorConfirmation" type="checkbox" />
        <span>
          {{
            $t(
              'AGENT_MGMT.PAUSE_TEMPLATES.FORM.REQUIRES_SUPERVISOR_CONFIRMATION'
            )
          }}
        </span>
      </label>

      <div class="flex justify-end gap-2">
        <Button
          v-if="isEditing"
          faded
          slate
          type="button"
          :label="$t('AGENT_MGMT.PAUSE_TEMPLATES.FORM.RESET')"
          @click="resetForm"
        />
        <Button
          type="submit"
          :label="
            isEditing
              ? $t('AGENT_MGMT.PAUSE_TEMPLATES.FORM.UPDATE')
              : $t('AGENT_MGMT.PAUSE_TEMPLATES.FORM.SUBMIT')
          "
          :is-loading="isSaving"
        />
      </div>
    </form>

    <div class="mt-4 border-t border-n-weak pt-4">
      <div v-if="isFetching" class="py-6 text-center text-sm text-n-slate-11">
        {{ $t('AGENT_MGMT.PAUSE_TEMPLATES.LOADING') }}
      </div>

      <div
        v-else-if="!templates.length"
        class="py-6 text-center text-sm text-n-slate-11"
      >
        {{ $t('AGENT_MGMT.PAUSE_TEMPLATES.EMPTY') }}
      </div>

      <div v-else class="flex flex-col gap-3">
        <div
          v-for="template in templates"
          :key="template.id"
          class="rounded-xl border border-n-weak p-4"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="flex flex-col gap-2">
              <div class="text-sm font-medium text-n-slate-12">
                {{ template.name }}
              </div>
              <div class="flex flex-wrap gap-2">
                <span
                  class="rounded-full bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
                >
                  {{
                    $t('AGENT_MGMT.PAUSE_TEMPLATES.LIST.DURATION', {
                      count: template.duration_in_minutes,
                    })
                  }}
                </span>
                <span
                  class="rounded-full bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
                >
                  {{
                    $t('AGENT_MGMT.PAUSE_TEMPLATES.LIST.DAILY_LIMIT', {
                      count: template.daily_limit,
                    })
                  }}
                </span>
                <span
                  class="rounded-full bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
                >
                  {{
                    template.requires_supervisor_confirmation
                      ? $t('AGENT_MGMT.PAUSE_TEMPLATES.LIST.APPROVAL_REQUIRED')
                      : $t('AGENT_MGMT.PAUSE_TEMPLATES.LIST.NO_APPROVAL')
                  }}
                </span>
              </div>
            </div>
            <div class="flex gap-2">
              <Button
                slate
                sm
                icon="i-woot-edit-pen"
                @click="editTemplate(template)"
              />
              <Button
                slate
                sm
                icon="i-woot-bin"
                class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                :is-loading="deletingId === template.id"
                @click="deleteTemplate(template)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
